/**
 * Copyright (C) 2019 Egylis,
 * Siège social, 83 bis rue de l'Abbé Groult, 75015 Paris - France.
 * All Rights Reserved.
 *
 * E-mail : info@egylis.com
 *
 * This software is the confidential and proprietary information
 * of Egylis. ("Confidential Information"). You shall not
 * disclose such Confidential Information and shall use it only
 * in accordance with the terms of the license agreement you
 * entered into with Egylis.
 *
 * Created by calvez on 18/03/19.
 */

import {Injectable} from '@angular/core';
import {ActivatedRoute, Router} from '@angular/router';
import {HttpClient} from '@angular/common/http';
import {Observable, Subject} from 'rxjs';
import {
    ArbreMachine,
    ArbreMachineMeta,
    ConfigService,
    FileData,
    FilterValue,
    FilterValueMeta,
    I18n,
    I18nMeta,
    Machine,
    MachineMeta,
    Media,
    MoI18nService,
    MpaService,
    Query,
    Rouleau,
    RouleauDoc,
    RouleauDocMeta,
    RouleauI18n,
    RouleauI18nMeta,
    RouleauMeta,
    RouleauNote,
    RouleauNoteMeta,
    SynchroStreamInfo,
    TravailMachine,
    TravailMachineMeta,
    MediaMeta
} from '../../common';
import {environment} from '../../environments/environment';
import {StatusEnum} from '../../common/entity';
import {Title} from '@angular/platform-browser';
import {FiltreUI, Identity, PageActionEnum, PageEnum, PageInfo, RouleauUI, UserPreferences} from '../entity/';
import {Constants} from './constant';
import {TranslateService} from '@ngx-translate/core';

/**
 * Data Service.
 */
@Injectable()
export class DataService {
    /**
     * Changed Identity Stream
     * @type {Subject<void>}
     */
    private changedIdentityStream = new Subject<void>();
    /**
     * Changed Page Stream
     * @type {Subject<void>}
     */
    private changedPageStream = new Subject<PageInfo>();
    /**
     * Page Title.
     */
    private _pageTitle: string = '';
    /**
     * Is started
     */
    private _isStarted: boolean = false;
    /**
     * Is logged
     */
    private _isLogged: boolean = false;
    /**
     * Identity.
     */
    private _identity: Identity = null;
    /**
     * Code Rouleau sélectionné
     */
    private selectedCodeRouleau: Array<string> = [];
    /**
     * User Filter.
     */
    private _userFilter: FiltreUI = {
        isFilterByMachine  : true,
        codeMachine        : null,
        filteredMachines   : [],
        typeTravail        : null,
        porteFilter        : {
            labelSelectCode: 'filterPage.toChoosePorte',
            labelCode      : 'filterPage.filterPorte',
            isFilter       : null,
            selectedCode   : null,
            codeValues     : null
        },
        largeurFilter      : {
            labelSelectCode: 'filterPage.toChooseLargeur',
            labelCode      : 'filterPage.filterLargeur',
            isFilter       : null,
            selectedCode   : null,
            codeValues     : null
        },
        chassisFilter      : {
            labelSelectCode: 'filterPage.toChooseChassis',
            labelCode      : 'filterPage.filterChassis',
            isFilter       : null,
            selectedCode   : null,
            codeValues     : null
        },
        timonFilter        : {
            labelSelectCode: 'filterPage.toChooseTimon',
            labelCode      : 'filterPage.filterTimon',
            isFilter       : null,
            selectedCode   : null,
            codeValues     : null
        },
        herseFilter        : {
            labelSelectCode: 'filterPage.toChooseHerse',
            labelCode      : 'filterPage.filterHerse',
            isFilter       : null,
            selectedCode   : null,
            codeValues     : null
        },
        roueFilter         : {
            labelSelectCode: 'filterPage.toChooseRoue',
            labelCode      : 'filterPage.filterRoue',
            isFilter       : null,
            selectedCode   : null,
            codeValues     : null
        },
        semoirFilter       : {
            labelSelectCode: 'filterPage.toChooseSemoir',
            labelCode      : 'filterPage.filterSemoir',
            isFilter       : null,
            selectedCode   : null,
            codeValues     : null
        },
        travailDisqueFilter: {
            labelSelectCode: 'filterPage.toChooseTravailDisque',
            labelCode      : 'filterPage.filterTravailDisque',
            isFilter       : null,
            selectedCode   : null,
            codeValues     : null
        },
        travailHerseFilter : {
            labelSelectCode: 'filterPage.toChooseTravailHerse',
            labelCode      : 'filterPage.filterTravailHerse',
            isFilter       : null,
            selectedCode   : null,
            codeValues     : null
        },
        travailDentFilter  : {
            labelSelectCode: 'filterPage.toChooseTravailDent',
            labelCode      : 'filterPage.filterTravailDent',
            isFilter       : null,
            selectedCode   : null,
            codeValues     : null
        },
        sols               : [],
        utilisations1      : [],
        utilisations2      : [],
    };
    /**
     * User Filter.
     */
    private _userPreferences: UserPreferences = {
        ponderationUtilisation1: 3,
        ponderationUtilisation2: 1,
        lang                   : null
    };

    /**
     * Constructor
     * @param http
     * @param router
     * @param route
     * @param mpaService
     * @param titleService
     * @param configService
     * @param moI18Service
     * @param translateService
     */
    constructor(private http: HttpClient,
                private router: Router,
                private route: ActivatedRoute,
                private mpaService: MpaService,
                private titleService: Title,
                private configService: ConfigService,
                private moI18Service: MoI18nService,
                private translateService: TranslateService) {
        this.initData();
    }


    /**
     * init Data
     */
    private initData(): void {
        const promiseCR = this.configService.getConfig('APP_SELECTED_ROULEAUX');
        const promiseUF = this.configService.getConfig('APP_USER_FILTER');
        const promiseUP = this.configService.getConfig('APP_USER_PREFERENCES');
        promiseCR.then(
            (val) => {
                if (val != null) {
                    this.selectedCodeRouleau = <Array<string>>val;
                }
            },
            () => {
                // TODO gérer l'erreur
            }
        );
        promiseUF.then(
            (val) => {
                if (val != null) {
                    this._userFilter = <FiltreUI>val;
                }
            },
            () => {
                // TODO gérer l'erreur
            }
        );
        promiseUP.then(
            (val) => {
                if (val != null) {
                    this._userPreferences = <UserPreferences>val;
                }
                if (this._userPreferences.lang != null) {
                    this.moI18Service.setLang(this._userPreferences.lang);
                } else {
                    this.computeDefaultLang();
                }
            },
            () => {
                // TODO gérer l'erreur
            }
        );
        Promise.all([promiseCR, promiseUF, promiseUP])
            .then(
                () => {
                    this.mpaService.isReadyToStart()
                        .then(
                            () => {
                                this.mpaService.initSynchro(
                                    environment.syncServerUrl,
                                    environment.syncApiKey,
                                    120000,
                                    true,
                                    100
                                );
                            },
                            () => {
                                // Error
                                // TODO Que fait-on ?
                            }
                        );
                    this._isStarted = true;
                },
                () => {
                    // TODO gérer l'erreur
                }
            );
    }

    /**
     * Return a promise who resolve when it's ready to start.
     * @return {Promise<StatusEnum>}
     */
    public isReadyToStart(): Promise<StatusEnum> {
        console.log('=========== MpaService isReadyToStart');
        return new Promise(
            (resolve, reject) => {
                this.mpaService.isReadyToStart().then(
                    () => {
                        this.isStartedTimeout(resolve);
                    }
                    ,
                    x => {
                        reject(x);
                    }
                );
            });
    }

    /**
     * is Started with a timeout
     * @param promiseResolve
     */
    private isStartedTimeout(promiseResolve: (r: StatusEnum) => void): void {
        console.log('-- MpaService isStartedTimeout');
        if (this._isStarted) {
            console.log('-- MpaService isStartedTimeout -- OK');
            promiseResolve(StatusEnum.OK);
        } else {
            console.log('-- MpaService isStartedTimeout -- TimeOut');
            setTimeout(this.isStartedTimeout.bind(this, promiseResolve), 100);
        }
    }

    /**
     * Is Login with promise.
     * @return {Promise<boolean>}
     */
    public isLoginWithPromise(): Promise<boolean> {
        return new Promise(
            (resolve) => {
                this.isLoginWithPromiseTimeout(resolve, false);
            });
    }

    /**
     * Internal Method for Is Login with promise.
     * @param promiseResolve
     * @param isAdmin true for isAdmin, false for isLogin
     */
    private isLoginWithPromiseTimeout(promiseResolve: (r: boolean) => void,
                                      isAdmin: boolean): void {
        if (this._isStarted) {
            promiseResolve(this._isLogged);
        } else {
            console.log('isLoginWithPromiseTimeout TIMEOUT');
            setTimeout(this.isLoginWithPromiseTimeout.bind(this, promiseResolve, isAdmin), 100);
        }
    }


    /**
     * Publish a new page Title.
     * @param pageTitle New Page Title
     * @param pageEnum Page
     */
    public publishNewPageTitle(pageEnum: PageEnum, pageTitle: string): void {
        this._pageTitle = pageTitle;
        this.titleService.setTitle('Lemken ' + pageTitle);
        this.changedPageStream.next(
            {
                page  : pageEnum,
                action: PageActionEnum.NEW_PAGE
            }
        );
    }

    /**
     * Publish a new section.
     * @param sect New section
     * @param pageEnum Page
     */
    public publishNewSection(pageEnum: PageEnum, sect: string): void {
        this.changedPageStream.next(
            {
                page   : pageEnum,
                action : PageActionEnum.NEW_SECTION,
                section: sect
            }
        );
    }

    /**
     * Get the observable of the changed identity stream.
     * @return {Observable<void>}
     */
    public getChangedIdentityStream(): Observable<void> {
        return this.changedIdentityStream.asObservable();
    }

    /**
     * Get the observable of the changed page stream.
     * @return {Observable<void>}
     */
    public getChangedPageStream(): Observable<PageInfo> {
        return this.changedPageStream.asObservable();
    }

    /**
     * Get the pageTitle.
     */
    public get pageTitle(): string {
        return this._pageTitle;
    }


    /**
     * Clean caches, tempory files...  for a new page.
     */
    public cleanNewPage(): void {
        this.mpaService.cleanNewPage();
    }


    /**
     * Get File
     * @param path the path of the file.
     */
    public getFile(path: string): Promise<FileData> {
        return this.mpaService.getFile(path,  true, true, true);
    }

    /**
     * Get Is Logged Indicator.
     */
    public get isLogged(): boolean {
        return this._isLogged;
    }

    /**
     * Getter Identity.
     */
    public get identity(): Identity {
        return this._identity;
    }


    /**
     * Comptue the uri for FileData
     * @param fileData
     */
    public computeUriForFileData(fileData: FileData): void {
        this.mpaService.computeUriForFileData(fileData);
    }


    /**
     * Stop the current Synchro
     */
    public stopSynchro(): void {
        this.mpaService.stopSynchro();
    }

    /**
     * Start the current Synchro
     */
    public startSynchro(): void {
        this.mpaService.startSynchro();
    }

    /**
     * Is syncing ?
     */
    public isSyncing(): boolean {
        return this.mpaService.isSyncing();
    }

    /**
     * Is an auto sync
     */
    public isAutoSync(): boolean {
        return this.mpaService.isAutoSync();
    }

    /**
     * Start autoSync
     */
    public startAutoSync(): void {
        this.mpaService.startAutoSync();
    }

    /**
     * Stop autoSync
     */
    public stopAutoSync(): void {
        this.mpaService.stopAutoSync();
    }

    /**
     * Get the observable of the Info stream.
     * @return {Observable<SynchroStreamInfo>}
     */
    public getInfoStream(): Observable<SynchroStreamInfo> {
        return this.mpaService.getInfoStream();
    }

    /**
     * Is Sync Table : est-ce les tables ont été synchronisées ?
     */
    public isSyncTableOnTime(): boolean {
        return this.mpaService.isSyncTableOnTime(Constants.TABLE_ONE_TIME);
    }


    /**
     * Get rouleaux UI
     */
    public getRouleauxUI(): Promise<Array<RouleauUI>> {
        return new Promise(
            (resolve, reject) => {
                const rouleauUIS: Array<RouleauUI> = [];
                let isOk: boolean = true;
                let docs: Array<RouleauDoc>;
                let rouleaux: Array<Rouleau>;
                let i18ns: Array<RouleauI18n>;
                let notes: Array<RouleauNote>;

                const promiseR = this.mpaService.executeQuery(new Query(RouleauMeta.instance));
                const promiseD = this.mpaService.executeQuery(new Query(RouleauDocMeta.instance));
                const promiseI = this.mpaService.executeQuery(new Query(RouleauI18nMeta.instance));
                const promiseN = this.mpaService.executeQuery(new Query(RouleauNoteMeta.instance));

                promiseR.then(
                    (res) => {
                        rouleaux = res;
                    },
                    () => {
                        isOk = false;
                    }
                );
                promiseD.then(
                    (res) => {
                        docs = res;
                    },
                    () => {
                        isOk = false;
                    }
                );
                promiseI.then(
                    (res) => {
                        i18ns = res;
                    },
                    () => {
                        isOk = false;
                    }
                );
                promiseN.then(
                    (res) => {
                        notes = res;
                    },
                    () => {
                        isOk = false;
                    }
                );
                Promise.all([promiseR, promiseD, promiseI, promiseN])
                    .then(
                        () => {
                            if (isOk) {
                                for (const rl of rouleaux) {
                                    if (rl.actif) {
                                        const rlNote = notes.find(value => value.codeRouleau === rl.code);
                                        const rlDocs = docs.filter(value => value.codeRouleau === rl.code);
                                        const rlI18ns = i18ns.filter(value => value.codeRouleau === rl.code);
                                        const rlDocs4Lang = rlDocs.filter(value => value.lang === MoI18nService.DEFAULT_LANG);
                                        let rlI18n = rlI18ns.find(value => value.lang === MoI18nService.DEFAULT_LANG);
                                        if (rlI18n == null) {
                                            rlI18n = new RouleauI18n();
                                            rlI18n.codeRouleau = rl.code;
                                            rlI18n.nom = rl.nom;
                                            rlI18n.description = rl.description;
                                        }
                                        if (rlNote != null) {
                                            rouleauUIS.push(
                                                {
                                                    rouleau      : rl,
                                                    note         : rlNote,
                                                    description  : rlI18n,
                                                    i18n         : rlI18ns,
                                                    docs         : rlDocs4Lang,
                                                    allDocs      : rlDocs,
                                                    selected     : this.selectedCodeRouleau.some(value => value === rl.code),
                                                    percentage   : 100,
                                                    viewDetail   : false,
                                                    viewSoilType : false,
                                                    score        : 0,
                                                    arbreMachines: [],
                                                    infos        : []
                                                }
                                            );
                                        }

                                    }
                                }
                                rouleauUIS.sort((a, b) => a.rouleau.nom.localeCompare(b.rouleau.nom));
                                resolve(rouleauUIS);
                            } else {
                                reject();
                            }
                        },
                        () => {
                            reject();
                        }
                    );

            }
        );

    }

    /**
     * Get Machine
     */
    public getMachines(): Promise<Array<Machine>> {
        return new Promise(
            (resolve, reject) => {
                let isOk: boolean = true;
                let machines: Array<Machine>;
                const promiseM = this.mpaService.executeQuery(new Query(MachineMeta.instance));
                promiseM.then(
                    (res) => {
                        machines = res;
                    },
                    () => {
                        isOk = false;
                    }
                );
                Promise.all([promiseM])
                    .then(
                        () => {
                            if (isOk) {
                                machines = machines.filter(value => value.actif);
                                machines.sort((a, b) => a.rang - b.rang);
                                resolve(machines);
                            } else {
                                reject();
                            }
                        },
                        () => {
                            reject();
                        }
                    );
            }
        );
    }

    /**
     * Get Arbre Machine
     */
    public getArbreMachines(): Promise<Array<ArbreMachine>> {
        return this.mpaService.executeQuery(new Query(ArbreMachineMeta.instance));
    }

    /**
     * Get Travail Machine
     */
    public getTravailMachines(): Promise<Array<TravailMachine>> {
        return this.mpaService.executeQuery(new Query(TravailMachineMeta.instance));
    }

    /**
     * Get Filter Values
     */
    public getFilterValues(): Promise<Array<FilterValue>> {
        return this.mpaService.executeQuery(new Query(FilterValueMeta.instance));
    }

    /**
     * Get I18ns
     */
    public getI18ns(): Promise<Array<I18n>> {
        return this.mpaService.executeQuery(new Query(I18nMeta.instance));
    }


    /**
     * Add the rouleau (by the code) in the selection
     * @param codeRouleau
     */
    public addSelection(codeRouleau: string): void {
        if (!this.selectedCodeRouleau.some(value => value === codeRouleau)) {
            this.selectedCodeRouleau.push(codeRouleau);
            this.configService.saveConfig('APP_SELECTED_ROULEAUX', this.selectedCodeRouleau).catch(() => {
            });
        }
    }

    /**
     * Remove the rouleau (by the code) in the selection
     * @param codeRouleau
     */
    public removeSelection(codeRouleau: string): void {
        if (this.selectedCodeRouleau.some(value => value === codeRouleau)) {
            this.selectedCodeRouleau = this.selectedCodeRouleau.filter(value => value !== codeRouleau);
            this.configService.saveConfig('APP_SELECTED_ROULEAUX', this.selectedCodeRouleau).catch(() => {
            });
        }
    }

    /**
     * Get the user Filter
     */
    public get userFilter(): FiltreUI {
        return this._userFilter;
    }

    /**
     * Save the user Filter
     * @param userFilter
     */
    public saveUserFilter(userFilter: FiltreUI): void {
        this._userFilter = userFilter;
        this.configService.saveConfig('APP_USER_FILTER', this._userFilter).catch(() => {
        });
    }

    /**
     * Get the user preferences
     */
    public get userPreferences(): UserPreferences {
        return this._userPreferences;
    }


    /**
     * Save the user Preferences
     * @param userPreferences
     */
    public saveUserPreferences(userPreferences: UserPreferences): Promise<{}> {
        this._userPreferences = userPreferences;
        if (this._userPreferences.lang != null) {
            this.moI18Service.setLang(this._userPreferences.lang);
        } else {
            this.computeDefaultLang();
        }
        return this.configService.saveConfig('APP_USER_PREFERENCES', this._userPreferences);
        // TODO changer langue
    }

    /**
     * Compute the default Lang.
     */
    private computeDefaultLang(): void {
        let lang = this.translateService.getBrowserLang();
        if (lang === null || lang.trim().length === 0) {
            lang = 'fr';
        }
        if (lang !== 'fr' && lang !== 'en' && lang !== 'de') {
            lang = 'fr';
        }
        this.moI18Service.setLang(lang);
    }

    /**
     * Get Docs.
     */
    public getDocs(): Promise<Array<RouleauDoc>> {
        return this.mpaService.executeQuery(new Query(RouleauDocMeta.instance));
    }

    /**
     * Get Media.
     */
    public getMedia(): Promise<Array<Media>> {
        return this.mpaService.executeQuery(new Query(MediaMeta.instance));
    }
}
